import styled from '@emotion/styled'
import { RefCallBack } from 'react-hook-form'
import { InputContainer } from 'modules/ui/atoms/Input/InputContainer'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import { DEFAULT_FOCUS_STYLES, DEFAULT_FONT_STYLES } from 'css/theme'
import { errorStyles, inputStyles } from 'css/utils'
import { forwardRef, useEffect, useId, useImperativeHandle, useState } from 'react'
import { useGetTokenexConfigQuery } from 'redux/services/tokenex'
import useConfig from 'modules/config/state/use-config'
import Bugsnag, { NotifiableError } from '@bugsnag/js'
import { useTranslation } from 'next-i18next'

export interface TokenexInputProps {
  type: 'ACH' | 'CC'
  label: string
  value: string
  error?: string
  onChange: (e: string) => void
  inputRef?: RefCallBack
}

type TokenexValidateResponse = {
  isValid: boolean
  validator?: 'format' | 'required' | 'customRegEx'
  characterCount?: number
}

type TokenexTokenizeResponse = {
  characterCount: number
  cvvIncluded: boolean
  referenceNumber: string
  token: string
  tokenHMAC: string
}

const styles = {
  base: `
    background:transparent;
    border:none;
    outline:none;
    padding: 0;
    height: 100%;
    font-family: ${DEFAULT_FONT_STYLES['--theme-font--base-font-family']};
    font-size: ${DEFAULT_FONT_STYLES['--theme-font--body-default__font-size']};
    font-weight: ${DEFAULT_FONT_STYLES['--theme-font--body-default__font-weight']};
    line-height: ${DEFAULT_FONT_STYLES['--theme-font--body-lg__line-height']};
    width:100%;
    vertical-align: sub;
  `,
}

type TokenexContainerProps = {
  focused: boolean
  errored: boolean
}

const ERROR_LABELS = {
  customRegEx: {
    ACH: 'Please enter a valid account number.',
    CC: 'Card number is invalid.',
  },
  format: {
    ACH: 'Please enter a valid account number.',
    CC: 'Card number is invalid.',
  },
  required: {
    ACH: 'Account number is required.',
    CC: 'Card number is required.',
  },
}

const focusStyles = (props: TokenexContainerProps) => {
  const { focused } = props

  if (focused) {
    return {
      outline: DEFAULT_FOCUS_STYLES['--theme--focus-outline'],
      outlineOffset: '1px',
    }
  }

  return {}
}

const TokenexContainer = styled.div(
  { height: '42px', position: 'relative', ...inputStyles },
  focusStyles,
  errorStyles,
)

interface TokenexIframe {
  focus: () => void
  validate: () => void
  tokenize: () => void
}

const TokenexInput = forwardRef(
  ({ type, label, value, error, onChange }: TokenexInputProps, ref) => {
    const { t } = useTranslation()
    const [isFocused, setIsFocused] = useState(false)
    const [tokenexIframe, setTokenexIframe] = useState<TokenexIframe | undefined>()
    const [tokenexError, setTokenexError] = useState<string>()

    const id = useId()
    const captionId = useId()

    const config = useConfig()

    const origin = encodeURIComponent(window.location.origin)

    const { data, isSuccess, isLoading } = useGetTokenexConfigQuery({
      campaignId: config.campaignId,
      origin,
      type,
    })

    useEffect(() => {
      if (!window.TokenEx) return

      if (isSuccess) {
        const config = JSON.parse(JSON.stringify({ ...data, styles }))
        config.customDataLabel = label
        const TokenexIframe = new window.TokenEx.Iframe(id, config)

        const handleValidateOnBlur = (data: TokenexValidateResponse) => {
          const { isValid, validator } = data

          if (isValid) {
            setTokenexError(undefined)
          } else if (validator) {
            setTokenexError(
              t(`tokenexInput.errorLabels.${validator}.${type}`, ERROR_LABELS[validator][type]),
            )
          }
        }

        const handleTokenize = (data: TokenexTokenizeResponse) => {
          const { token } = data
          onChange(token)
        }

        TokenexIframe.on('validate', handleValidateOnBlur)
        TokenexIframe.on('tokenize', handleTokenize)

        TokenexIframe.on('focus', () => setIsFocused(true))

        TokenexIframe.on('blur', () => {
          setIsFocused(false)

          TokenexIframe.validate()
          TokenexIframe.tokenize()
        })

        TokenexIframe.on('error', (e: NotifiableError) => {
          console.error(e)
          Bugsnag.notify(e)
        })

        TokenexIframe.load()
        setTokenexIframe(TokenexIframe)
      }
    }, [origin, data, isSuccess, id, onChange, type, label, window.TokenEx])

    useImperativeHandle(ref, () => ({
      focus: () => tokenexIframe?.focus(),
    }))

    return (
      <InputContainer
        id={id}
        captionId={captionId}
        value={value}
        label={label}
        required
        error={tokenexError ? tokenexError : error}
      >
        <TokenexContainer
          id={id}
          errored={!!error || !!tokenexError}
          focused={isFocused}
          aria-describedby={captionId}
          onMouseLeave={() => {
            if (tokenexIframe) {
              tokenexIframe.validate()
              tokenexIframe.tokenize()
            }
          }}
        >
          {isLoading && (
            <Placeloader
              style={{
                width: '100%',
                height: '42px',
                position: 'absolute',
                top: 0,
                left: 0,
                borderRadius: 8,
              }}
            />
          )}
        </TokenexContainer>
      </InputContainer>
    )
  },
)

TokenexInput.displayName = 'TokenexInput'

export default TokenexInput
